import store, { FeatureToggles } from "@two-ui/store";
import { RouteLocationNormalized } from "vue-router";
import { SIDE_BAR_ITEMS } from "@two-ui/constants";
import { SideBarItemsType } from "@two-ui/types";
import { useGlobalStore } from "@two-ui/global-store";

export class FeatureToggleNavigationError extends Error {
  constructor(message?: string) {
    super(message);
  }
}
/**
 * Checks if route requires feature toggle and router meta is set.
 * If it requires feature toggle and router meta not set, if route opened directly via url, it returns to home page. If opened
 * using router navigation, throws customer error that can be handled using router.onError e.g. to show some feedback.
 * Returns undefined if there is no issue with performing the navigation.
 * @param to
 * @param from
 * @returns route or undefined
 */
export default async function featureToggleRestrictionGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) {
  const sidebarItem = SIDE_BAR_ITEMS.find(
    (route) => route.routeName === to.name
  );
  if (sidebarItem?.isUserStaff) await store.waitForUserPropertiesToPopulate();
  const requiresMetaRoute = to.meta.requiresFeatureToggle
    ?.feature as keyof FeatureToggles;
  if (
    !isEnabledSideBar(sidebarItem) ||
    (requiresMetaRoute && !store.state.featureToggles[requiresMetaRoute])
  ) {
    if (!from.name) return { name: "home" };
    throw new FeatureToggleNavigationError();
  }
}

export const customerHasBalancesEnabled = (sideBarItem?: SideBarItemsType) => {
  if (
    sideBarItem?.routeName === "currency-exchange" &&
    (!store.state.customerFeatureFlags?.floats ||
      !store.state.customerFeatureFlags?.fx)
  ) {
    return false;
  }
  return true;
};

export const customerHasFraudEnabled = (sideBarItem?: SideBarItemsType) => {
  if (sideBarItem?.routeName === "security-alerts") {
    return store.state.customerFeatureFlags?.enableFraudBlocking ?? false;
  }

  return true;
};

export const isEnabledSideBar = (sideBarItem?: SideBarItemsType) => {
  if (!sideBarItem) return true;

  // First handle staff features
  if (sideBarItem.isUserStaff) {
    // Check if user is staff and feature is enabled via propertyStore
    if (sideBarItem.propertyStore) {
      const propertyStore = sideBarItem.propertyStore as keyof FeatureToggles;
      return (
        store.state.isUserStaff && store.state.featureToggles[propertyStore]
      );
    }
    return store.state.isUserStaff;
  }

  if (sideBarItem.enableForCustomers) {
    const customers = sideBarItem.enableForCustomers;
    const currentCustomer = store.getCustomerId();

    if (!currentCustomer || !customers.includes(currentCustomer)) {
      return false;
    }
  }

  if (sideBarItem.disableForCustomers) {
    const customers = sideBarItem.disableForCustomers;
    const currentCustomer = store.getCustomerId();

    if (currentCustomer && customers.includes(currentCustomer)) {
      return false;
    }
  }

  // Then handle playground mode
  const globalStore = useGlobalStore();
  if (globalStore.playgroundMode) {
    return !!sideBarItem.allowInPlaygroundMode;
  }

  if (
    sideBarItem.routeName === "product-approval-home" &&
    !globalStore.isCustomerAllowedManualBrandApproval()
  ) {
    return false;
  }

  // For non-staff features, check propertyStore
  if (!sideBarItem.propertyStore) return true;
  const propertyStore = sideBarItem.propertyStore as keyof FeatureToggles;
  return store.state.featureToggles[propertyStore];
};
